import { useState, ReactNode } from "react";
import { BasicDialog } from "./BasicDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface ConfirmDialogProps {
  /** Controls dialog open/close state */
  open: boolean;
  /** Callback when dialog state changes */
  onOpenChange: (open: boolean) => void;
  /** Callback when user confirms action */
  onConfirm: () => void | Promise<void>;

  /** Dialog title text */
  title: string;
  /** Dialog description text */
  description: string;

  /** Confirm button text (default: "Confirm") */
  confirmText?: string;
  /** Cancel button text (default: "Cancel") */
  cancelText?: string;

  /** Variant for styling */
  variant?: "default" | "destructive" | "warning";

  /** Require text confirmation for destructive actions */
  requireTextConfirmation?: boolean;
  /** Word that must be typed to confirm (default: "DELETE") */
  confirmationWord?: string;
  /** Placeholder for confirmation input */
  confirmationPlaceholder?: string;
  /** Label for confirmation input */
  confirmationLabel?: string;

  /** Show loading state on confirm button */
  loading?: boolean;

  /** Additional metadata/content to display */
  metadata?: ReactNode;

  /** Icon to display in title */
  icon?: ReactNode;
}

/**
 * Reusable confirmation dialog component built on BasicDialog.
 * Supports simple confirmations and destructive actions with text confirmation.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  requireTextConfirmation = false,
  confirmationWord = "DELETE",
  confirmationPlaceholder,
  confirmationLabel,
  loading = false,
  metadata,
  icon,
}: ConfirmDialogProps) {
  const [inputValue, setInputValue] = useState("");

  const isValid = !requireTextConfirmation || inputValue === confirmationWord;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setInputValue("");
    }
    onOpenChange(newOpen);
  };

  const handleConfirm = async () => {
    if (isValid) {
      await onConfirm();
      setInputValue("");
    }
  };

  const tone = variant === "destructive" ? "destructive" : variant === "warning" ? "warning" : "default";

  return (
    <BasicDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      icon={icon}
      tone={tone}
      dismiss={{
        label: cancelText,
        onClick: () => {
          setInputValue("");
          onOpenChange(false);
        },
      }}
      confirm={{
        label: confirmText,
        onClick: handleConfirm,
        disabled: !isValid || loading,
        loading,
        loadingLabel: undefined,
        variant: variant === "destructive" ? "destructive" : "default",
      }}
      closeOnOutsideClick={variant !== "destructive"}
    >
      <div className="space-y-3">
        <p className={variant === "destructive" || variant === "warning" ? "font-semibold text-foreground" : "text-muted-foreground"}>
          {description}
        </p>

        {metadata}

        {requireTextConfirmation && (
          <div className="space-y-2">
            <Label htmlFor="confirm-text">
              {confirmationLabel || `Type ${confirmationWord} to confirm`}
            </Label>
            <Input
              id="confirm-text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={confirmationPlaceholder || confirmationWord}
              disabled={loading}
              className="font-mono"
            />
          </div>
        )}
      </div>
    </BasicDialog>
  );
}
